import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Button, Icon } from "semantic-ui-react";

const escaparCsv = valor => `"${String(valor === undefined || valor === null ? "" : valor).replace(/"/g, '""')}"`;

// Mostra o resultado da importação: resumo, erros por tipo, linhas importadas e linhas com erro.
const ResultadoImportacao = ({ resultado, erroGlobal, carregando, condominioId, enviouEmail, novoToken, onNova, onTentarNovamente }) => {
  const importadas = resultado.data || [];
  const comErro = resultado.errors || [];
  const [aba, setAba] = useState("importadas");
  const [busca, setBusca] = useState("");

  // abre direto na aba "Com erro" quando o resultado chega com erros
  useEffect(() => {
    setAba(comErro.length > 0 ? "erros" : "importadas");
  }, [comErro.length, importadas.length]);

  const errosPorTipo = useMemo(() => {
    const mapa = {};
    comErro.forEach(item =>
      (item.collumnsError || []).forEach(err => {
        const chave = `${err.field}|${err.message}`;
        mapa[chave] = mapa[chave] || { campo: err.field, mensagem: err.message, qtd: 0 };
        mapa[chave].qtd += 1;
      })
    );
    return Object.values(mapa).sort((a, b) => b.qtd - a.qtd);
  }, [comErro]);

  const termo = busca.trim().toLowerCase();
  const importadasVisiveis = termo
    ? importadas.filter(i => [i.documento, i.nome, i.email, i.bloco, i.numero].join(" ").toLowerCase().includes(termo))
    : importadas;
  const comErroVisiveis = termo
    ? comErro.filter(i => String(i.row).toLowerCase().includes(termo) || String(i.rowNumber) === termo ||
        (i.collumnsError || []).some(e => `${e.field} ${e.message}`.toLowerCase().includes(termo)))
    : comErro;

  const baixarErros = () => {
    const linhas = [["linha", "dados", "campo", "problema"]];
    comErro.forEach(item =>
      (item.collumnsError || []).forEach(err => linhas.push([item.rowNumber, item.row, err.field, err.message]))
    );
    const csv = linhas.map(l => l.map(escaparCsv).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "linhas-com-erro.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const total = resultado.rowCount || 0;
  let estado = null;
  if (erroGlobal) estado = "falha";
  else if (!carregando && total > 0) estado = comErro.length === 0 ? "sucesso" : importadas.length === 0 ? "erro" : "parcial";

  return (
    <div className="va-res">

      {carregando && (
        <div className="va-res-banner info"><Icon loading name="spinner" /> Processando o arquivo...</div>
      )}

      {erroGlobal && (
        <div className="va-res-banner falha" role="alert">
          <Icon name="times circle outline" size="big" />
          <div>
            <b>{erroGlobal.titulo}</b>
            <p>{erroGlobal.detalhe}</p>
            <div className="va-res-acoes">
              <Button size="small" className="btn-ui-1" onClick={onTentarNovamente}><Icon name="redo" /> Tentar novamente</Button>
              <Link to="/fale-conosco" className="va-res-link">Falar com o suporte</Link>
            </div>
          </div>
        </div>
      )}

      {estado === "sucesso" && (
        <div className="va-res-banner sucesso" role="status">
          <Icon name="check circle outline" size="big" />
          <div>
            <b>Importação concluída</b>
            <p>{importadas.length} linha{importadas.length !== 1 && "s"} importada{importadas.length !== 1 && "s"} sem nenhum erro.</p>
          </div>
        </div>
      )}
      {estado === "parcial" && (
        <div className="va-res-banner parcial" role="status">
          <Icon name="warning circle" size="big" />
          <div>
            <b>Importação concluída com avisos</b>
            <p>{importadas.length} linha{importadas.length !== 1 && "s"} importada{importadas.length !== 1 && "s"} e {comErro.length} com erro. Corrija as linhas com erro no arquivo e importe novamente.</p>
          </div>
        </div>
      )}
      {estado === "erro" && (
        <div className="va-res-banner falha" role="alert">
          <Icon name="times circle outline" size="big" />
          <div>
            <b>Nenhuma linha foi importada</b>
            <p>Todas as {comErro.length} linhas do arquivo têm erros. Veja o que corrigir abaixo.</p>
          </div>
        </div>
      )}

      {!erroGlobal && (
        <>
          {total > 0 && (
            <div className="va-res-progresso" role="img" aria-label={`${importadas.length} de ${total} linhas importadas`}>
              <div className="va-res-barra">
                <span className="ok" style={{ width: `${(importadas.length / total) * 100}%` }} />
                <span className="erro" style={{ width: `${(comErro.length / total) * 100}%` }} />
              </div>
              <small>{Math.round((importadas.length / total) * 100)}% das linhas importadas</small>
            </div>
          )}

          <div className="va-res-cartoes">
            <div className="va-res-cartao"><small>Linhas no arquivo</small><b>{total}</b></div>
            <div className="va-res-cartao ok"><small>Importadas</small><b>{importadas.length}</b></div>
            <div className={`va-res-cartao ${comErro.length ? "erro" : ""}`}><small>Com erro</small><b>{comErro.length}</b></div>
          </div>

          {errosPorTipo.length > 0 && (
            <div className="va-res-resumo-erros">
              <h4>O que corrigir</h4>
              <ul>
                {errosPorTipo.map(e => (
                  <li key={`${e.campo}-${e.mensagem}`}>
                    <span className="va-res-campo">{e.campo}</span> {e.mensagem}
                    <em>{e.qtd} linha{e.qtd !== 1 && "s"}</em>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!carregando && total > 0 && (
            <div className="va-res-proximos">
              <h4>Próximos passos</h4>
              <ul>
                {importadas.length > 0 && (
                  <li>
                    <Icon name={enviouEmail ? "mail" : "mail outline"} />
                    {enviouEmail
                      ? "Os e-mails com o token de acesso foram enviados aos responsáveis importados."
                      : <>Os e-mails ainda não foram enviados. Envie o token em <Link to={`/condominios/${condominioId}/unidades`}>Unidades votantes → Enviar token para todas</Link>.</>}
                  </li>
                )}
                {importadas.length > 0 && novoToken && (
                  <li><Icon name="key" /> Foi gerado um novo token para as unidades que já existiam; o anterior deixou de valer.</li>
                )}
                {comErro.length > 0 && (
                  <li><Icon name="edit outline" /> Baixe a lista de erros, corrija essas linhas na planilha e importe novamente — as linhas já importadas serão apenas atualizadas.</li>
                )}
                {comErro.length === 0 && importadas.length > 0 && (
                  <li><Icon name="check" /> Nada mais a corrigir: confira as unidades cadastradas na lista de unidades votantes.</li>
                )}
              </ul>
            </div>
          )}

          <div className="va-res-acoes-topo">
            <Button className="btn-ui-1" onClick={onNova}><Icon name="redo" /> Nova importação</Button>
            {importadas.length > 0 && (
              <Link to={`/condominios/${condominioId}/unidades`} className="ui basic button"><Icon name="home" /> Ver unidades votantes</Link>
            )}
            {comErro.length > 0 && (
              <Button basic onClick={baixarErros}><Icon name="download" /> Baixar lista de erros</Button>
            )}
          </div>

          {(importadas.length > 0 || comErro.length > 0) && (
            <>
              <div className="va-un-busca va-res-busca">
                <Icon name="search" />
                <input type="search" placeholder="Buscar por nome, documento, e-mail ou linha" value={busca} onChange={e => setBusca(e.target.value)} aria-label="Buscar no resultado" />
              </div>
              <div className="va-res-abas" role="tablist">
                <button type="button" role="tab" aria-selected={aba === "importadas"} className={aba === "importadas" ? "ativo ok" : ""} onClick={() => setAba("importadas")}>
                  <Icon name="check" /> Importadas <span>{importadas.length}</span>
                </button>
                <button type="button" role="tab" aria-selected={aba === "erros"} className={aba === "erros" ? "ativo erro" : ""} onClick={() => setAba("erros")}>
                  <Icon name="times" /> Com erro <span>{comErro.length}</span>
                </button>
                {aba === "erros" && comErro.length > 0 && (
                  <button type="button" className="va-res-baixar" onClick={baixarErros}><Icon name="download" /> Baixar lista de erros</button>
                )}
              </div>

              {aba === "importadas" && (
                importadas.length === 0 ? <div className="va-res-vazio">Nenhuma linha foi importada.</div> : (
                  <div className="va-res-tabela-wrap">
                    <table className="va-res-tabela">
                      <thead><tr><th>Documento</th><th>Responsável</th><th>Unidade votante</th></tr></thead>
                      <tbody>
                        {importadasVisiveis.map(item => (
                          <tr key={`${item.documento}-${item.bloco}-${item.numero}`}>
                            <td>{item.documento}</td>
                            <td><b>{item.nome}</b><small>{item.email}</small></td>
                            <td>{[item.bloco, item.numero].filter(Boolean).join(" · ")}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {aba === "erros" && (
                comErro.length === 0 ? <div className="va-res-vazio">Nenhuma linha com erro.</div> : (
                  <div className="va-res-tabela-wrap">
                    <table className="va-res-tabela">
                      <thead><tr><th style={{ width: 70 }}>Linha</th><th>Problema</th><th>Dados da linha</th></tr></thead>
                      <tbody>
                        {comErroVisiveis.map(item => (
                          <tr key={item.rowNumber}>
                            <td><span className="va-res-linha">{item.rowNumber}</span></td>
                            <td>
                              {(item.collumnsError || []).map(err => (
                                <div key={`${err.field}-${err.message}`} className="va-res-erro">
                                  <b>{err.field}</b> {err.message}
                                  {err.options && <small>{err.options}</small>}
                                </div>
                              ))}
                            </td>
                            <td><code>{item.row}</code></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

ResultadoImportacao.propTypes = {
  resultado: PropTypes.object.isRequired,
  erroGlobal: PropTypes.shape({ titulo: PropTypes.string, detalhe: PropTypes.string }),
  carregando: PropTypes.bool,
  condominioId: PropTypes.string,
  enviouEmail: PropTypes.bool,
  novoToken: PropTypes.bool,
  onNova: PropTypes.func.isRequired,
  onTentarNovamente: PropTypes.func.isRequired,
};

export default ResultadoImportacao;
